import { useEffect, useMemo, useState, useCallback } from "react";
import JobLink from "@/components/JobLink";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function OTIFReport() {
  const [customers, setCustomers] = useState([]);
  const [customerFilter, setCustomerFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [newSla, setNewSla] = useState(15);
  const [repeatSla, setRepeatSla] = useState(10);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState("customer"); // customer | detail

  useEffect(() => {
    api.get("/reports/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  const load = () => {
    setBusy(true);
    const params = { new_sla_days: newSla, repeat_sla_days: repeatSla };
    if (customerFilter !== "all") params.customer_name = customerFilter;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/reports/otif", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerFilter]);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) => (r.customer_name || "").toLowerCase().includes(n));
    }
    return list;
  }, [data, q]);

  const detailRows = useMemo(() => {
    if (!data) return [];
    let list = data.detail_rows;
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.customer_name || "").toLowerCase().includes(n) ||
        (r.mpwo_number || "").toLowerCase().includes(n) ||
        (r.product_name || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [data, q]);

  const grand = data?.grand || {};

  const exportCSV = () => {
    if (view === "customer") {
      downloadCSV(
        "otif-late-delivery-by-customer",
        ["Customer Name", "Time Period", "Total Jobs Dispatched", "New Jobs", "Repeat Jobs",
         "On-Time (New)", "On-Time (Repeat)", "On-Time Total", "OTIF %", "Avg Days Taken"],
        rows.map((r) => [
          r.customer_name,
          `${from || "—"} to ${to || "—"}`,
          r.total_dispatched, r.new_jobs, r.repeat_jobs,
          r.on_time_new, r.on_time_repeat, r.on_time_total, r.otif_pct, r.avg_days_taken,
        ])
      );
    } else {
      downloadCSV(
        "otif-late-delivery-detail",
        ["Customer", "MPWO", "Product", "Type", "SLA Days", "Order Date", "Dispatch Date", "Days Taken", "Qty", "Balance Qty", "On Time", "In Full", "OTIF"],
        detailRows.map((r) => [
          r.customer_name, r.mpwo_number, r.product_name, r.job_type, r.sla_days,
          (r.order_date || "").slice(0, 10), (r.dispatch_date || "").slice(0, 10),
          r.days_taken, r.quantity, r.balance_qty,
          r.on_time ? "Yes" : "No", r.in_full ? "Yes" : "No", r.otif ? "Yes" : "No",
        ])
      );
    }
  };

  const otifTone = grand.otif_pct >= 95 ? "ok" : grand.otif_pct >= 80 ? "warn" : "danger";

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Customer</label>
          <div className="flex items-center gap-1">
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger data-testid="otif-customer" className="rounded-sm w-64"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="all">All customers</SelectItem>
                {customers.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {customerFilter !== "all" && (
              <button data-testid="otif-clear-customer" onClick={() => setCustomerFilter("all")}
                className="p-1.5 hover:bg-neutral-100 rounded-sm" title="Clear"><X size={14} /></button>
            )}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">From (dispatch)</label>
          <Input data-testid="otif-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div>
          <label className="label-caps block mb-1">To (dispatch)</label>
          <Input data-testid="otif-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" />
        </div>
        <div className="border border-neutral-200 rounded-sm bg-amber-50/60 p-2 flex items-end gap-2">
          <div>
            <label className="label-caps block mb-1 text-[10px]">Preset SLA · New</label>
            <div className="flex items-center gap-1">
              <Input data-testid="otif-new-sla" type="number" min={1} value={newSla} onChange={(e) => setNewSla(Number(e.target.value) || 15)} className="rounded-sm w-16 h-8 text-sm" />
              <span className="text-[10px] text-neutral-500">days</span>
            </div>
          </div>
          <div>
            <label className="label-caps block mb-1 text-[10px]">Repeat</label>
            <div className="flex items-center gap-1">
              <Input data-testid="otif-repeat-sla" type="number" min={1} value={repeatSla} onChange={(e) => setRepeatSla(Number(e.target.value) || 10)} className="rounded-sm w-16 h-8 text-sm" />
              <span className="text-[10px] text-neutral-500">days</span>
            </div>
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">View</label>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger data-testid="otif-view" className="rounded-sm w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">By customer</SelectItem>
              <SelectItem value="detail">Job-level detail</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-48">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="otif-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="customer / MPWO / product" />
          </div>
        </div>
        <Button data-testid="otif-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Apply"}
        </Button>
        <Button data-testid="otif-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-3 py-1.5 mb-3">
        <b>Preset SLA:</b> Repeat = {repeatSla} days · New = {newSla} days (measured from Order Received → Dispatch). Adjust above and hit Apply.
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi label="Total dispatched" value={inr(grand.total_dispatched || 0)} />
        <Kpi label="New jobs" value={inr(grand.new_jobs || 0)} />
        <Kpi label="Repeat jobs" value={inr(grand.repeat_jobs || 0)} />
        <Kpi label="On-time total" value={inr(grand.on_time_total || 0)} tone="ok" />
        <Kpi label="OTIF count" value={inr(grand.otif_count || 0)} />
        <Kpi label="OTIF %" value={`${grand.otif_pct || 0}%`} tone={otifTone} />
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        {view === "customer" ? (
          <table className="w-full text-sm" data-testid="otif-customer-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-left px-4 py-2.5">Customer Name</th>
                <th className="text-left px-4 py-2.5">Time Period</th>
                <th className="text-right px-4 py-2.5">Total Jobs Dispatched</th>
                <th className="text-right px-4 py-2.5">New Jobs</th>
                <th className="text-right px-4 py-2.5">Repeat Jobs</th>
                <th className="text-right px-4 py-2.5">On-Time (New)</th>
                <th className="text-right px-4 py-2.5">On-Time (Repeat)</th>
                <th className="text-right px-4 py-2.5">Avg Days Taken</th>
                <th className="text-right px-4 py-2.5">OTIF %</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <EmptyRow cols={9} msg="No dispatched jobs in this filter." />}
              {rows.slice(0, 500).map((r) => {
                const tone = r.otif_pct >= 95 ? "text-emerald-700"
                  : r.otif_pct >= 80 ? "text-amber-700"
                  : "text-rose-700 font-bold";
                return (
                  <tr key={r.customer_name} className="border-t border-neutral-100 data-row" data-testid={`otif-crow-${r.customer_name}`}>
                    <td className="px-4 py-2.5 font-medium">{r.customer_name}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-500">{from || "—"} → {to || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num font-semibold">{inr(r.total_dispatched)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{inr(r.new_jobs)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{inr(r.repeat_jobs)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.on_time_new)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-emerald-700">{inr(r.on_time_repeat)}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">{r.avg_days_taken != null ? r.avg_days_taken : "—"}</td>
                    <td className={`px-4 py-2.5 text-right font-mono-num ${tone}`}>{r.otif_pct}%</td>
                  </tr>
                );
              })}
              {rows.length > 500 && (
                <tr><td colSpan={9} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 500 of {rows.length} rows. Export CSV for full list.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm" data-testid="otif-detail-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">MPWO</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-right px-3 py-2">SLA</th>
                <th className="text-left px-3 py-2">Order Date</th>
                <th className="text-left px-3 py-2">Dispatch Date</th>
                <th className="text-right px-3 py-2">Days Taken</th>
                <th className="text-right px-3 py-2">Qty</th>
                <th className="text-center px-3 py-2">On Time</th>
                <th className="text-center px-3 py-2">In Full</th>
                <th className="text-center px-3 py-2">OTIF</th>
              </tr>
            </thead>
            <tbody>
              {detailRows.length === 0 && <EmptyRow cols={12} msg="No dispatched jobs in this filter." />}
              {detailRows.slice(0, 800).map((r, i) => {
                const daysTone = r.days_taken == null ? "text-neutral-400"
                  : r.days_taken <= r.sla_days ? "text-emerald-700 font-semibold"
                  : "text-rose-700 font-bold";
                const yesNo = (v, good) => (
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest ${
                    v ? (good ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800")
                       : "bg-rose-100 text-rose-800"
                  }`}>{v ? "Yes" : "No"}</span>
                );
                return (
                  <tr key={`${r.mpwo_number || i}-${i}`} className="border-t border-neutral-100 data-row" data-testid={`otif-drow-${r.mpwo_number || i}`}>
                    <td className="px-3 py-2">{r.customer_name}</td>
                    <td className="px-3 py-2 font-mono-num text-xs"><JobLink id={r.job_id} number={r.mpwo_number} /></td>
                    <td className="px-3 py-2 text-xs">{r.product_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${
                        r.job_type === "New" ? "bg-blue-50 text-blue-700 border border-blue-200"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      }`}>{r.job_type}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num text-neutral-600">{r.sla_days}d</td>
                    <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.order_date || "").slice(0, 10) || "—"}</td>
                    <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.dispatch_date || "").slice(0, 10) || "—"}</td>
                    <td className={`px-3 py-2 text-right font-mono-num ${daysTone}`}>{r.days_taken ?? "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{inr(r.quantity || 0)}</td>
                    <td className="px-3 py-2 text-center">{yesNo(r.on_time, true)}</td>
                    <td className="px-3 py-2 text-center">{yesNo(r.in_full, true)}</td>
                    <td className="px-3 py-2 text-center">{yesNo(r.otif, true)}</td>
                  </tr>
                );
              })}
              {detailRows.length > 800 && (
                <tr><td colSpan={12} className="px-4 py-3 text-center text-neutral-400 text-xs">Showing first 800 of {detailRows.length} rows. Export CSV for full list.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-3 text-xs text-neutral-500">
        <b>On-Time</b>: days taken (order → dispatch) ≤ SLA. <b>In Full</b>: balance_qty is 0. <b>OTIF</b>: both true.
      </p>
    </div>
  );
}


// ============================================================================
// 11. RM % Detail Report (per Job × per Material)
// ============================================================================
